import {
  changeGroupColor,
  changePouchColor,
  enableMuteSymbol,
  getActiveLocale,
  getTooltipCopy,
} from '../i18n';
import { GROUP_COLORS, POUCH_COLORS } from '../types';
import { getModifierKeys } from '../App';
import { GROUP_COLOR_HOTKEY_DISPLAY_ORDER } from './groupColorHotkeys';
import { POUCH_COLOR_HOTKEY_DISPLAY_ORDER } from './pouchColorHotkeys';

/** Guide オフ時はキー・マウス操作のツールチップを出さない（App が window.__isGuideEnabled を同期） */
export function isGuideTooltipActive(): boolean {
  return (window as any).__isGuideEnabled !== false;
}

const MODIFIER_PATTERN =
  /\b(Double Click|Long Press|Click|Shift|Ctrl|Alt|Option|Hold|Space|Delete|Del|Backspace|Drag|Enter|F2|Esc)\b|([⌘⌥⌃⇧])/g;

function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** 値・乗算・結果の <b> ブロック末尾にプレーン行を1行追加（lastExpression 用） */
export function appendLineToPouchNumericTooltipBoldBlock(
  html: string | undefined,
  plainLine: string | null | undefined
): string | undefined {
  if (!html || !plainLine?.trim()) return html;
  const escaped = escapeHtml(plainLine.trim());
  return html.replace(/<\/b>\s*$/, `<br/>${escaped}</b>`);
}

/** 値・乗算・結果・数式行（Guide オフでも表示。操作ガイド文言は含めない） */
export function formatPouchNumericTooltipBoldBlock(numericDetail: string | null | undefined): string {
  if (numericDetail == null || !String(numericDetail).trim()) return '';
  const inner = String(numericDetail)
    .split('\n')
    .map((line) => escapeHtml(line))
    .join('<br/>');
  return `<b>${inner}</b>`;
}

const SYMBOL_MAP: Record<string, string> = {
  Option: '⌥',
};

const ACTION_WORDS = new Set(['Click', 'Double Click', 'Hold', 'Long Press', 'Drag']);

/**
 * 「W / O / …」や「C / ⇧+C」など、スラッシュ区切りのキー列を <kbd> で囲む。
 * MODIFIER 処理の後に実行（⇧ が先に <kbd> 化された後の「+C」も扱う）
 */
function wrapSlashSeparatedKeys(escaped: string): string {
  return escaped
    .split(' / ')
    .map((segment) => {
      const t = segment.trim();
      if (/^[A-Za-z]$/.test(t)) {
        return `<kbd>${t}</kbd>`;
      }
      const comboPlain = t.match(/^(⇧|Shift)\+([A-Za-z])$/);
      if (comboPlain) {
        return `<kbd>${comboPlain[1]}</kbd>+<kbd>${comboPlain[2]}</kbd>`;
      }
      const comboAfterKbd = t.match(/^<kbd>(⇧|Shift)<\/kbd>\+([A-Za-z])$/);
      if (comboAfterKbd) {
        return `<kbd>${comboAfterKbd[1]}</kbd>+<kbd>${comboAfterKbd[2]}</kbd>`;
      }
      return segment;
    })
    .join(' / ');
}

function wrapKbd(escaped: string): string {
  return wrapSlashSeparatedKeys(
    escaped
      .replace(MODIFIER_PATTERN, (_, word, symbol) => {
        if (word && ACTION_WORDS.has(word)) {
          return `<kbd class="tt-action">${word}</kbd>`;
        }
        return `<kbd>${SYMBOL_MAP[word] || word || symbol}</kbd>`;
      })
      .replace(/<\/kbd>[+ ]<kbd/g, '</kbd><kbd'),
  );
}

function isMacPlatform(): boolean {
  return typeof navigator !== 'undefined' && navigator.platform.toUpperCase().indexOf('MAC') >= 0;
}

/** 操作ガイド用: Mac では Backspace キーを ⌫ と表記 */
function formatGuideBackspaceKeyHtml(): string {
  if (!isGuideTooltipActive()) return '';
  if (isMacPlatform()) {
    return '<kbd>⌫</kbd>';
  }
  return formatTooltip('Backspace');
}

/** PC 操作ガイド: パウチ — Edit 系（色系・Delete 系の前に並べる）。帳尻パウチは値編集不可のため Enter 行を省略可 */
export function formatPouchGuideEditPcExtrasHtml(opts?: { omitEnter?: boolean }): string {
  if (!isGuideTooltipActive()) return '';
  const tt = getTooltipCopy();
  if (opts?.omitEnter) {
    return `${formatTooltip('F2')} / ${formatTooltip('Space')} <span class="tt-desc">${escapeHtml(tt.editPouchTitle)}</span>`;
  }
  return [
    `${formatTooltip('Enter')}/<kbd>0</kbd>~<kbd>9</kbd>/<kbd>-</kbd> <span class="tt-desc">${escapeHtml(tt.editPouchValue)}</span>`,
    `${formatTooltip('F2')} / ${formatTooltip('Space')} <span class="tt-desc">${escapeHtml(tt.editPouchTitle)}</span>`,
  ].join('<br/>');
}

/** PC 操作ガイド: パウチ — Delete 系 */
export function formatPouchGuideDeletePcExtrasHtml(): string {
  if (!isGuideTooltipActive()) return '';
  const tt = getTooltipCopy();
  return `${formatTooltip('Del')} / ${formatGuideBackspaceKeyHtml()} <span class="tt-desc">${escapeHtml(tt.deletePouch)}</span>`;
}

/** PC 操作ガイド: パウチホバー 2 秒後（Edit → Delete ※色は別ブロックで表示） */
export function formatPouchGuidePcExtrasHtml(): string {
  if (!isGuideTooltipActive()) return '';
  return `${formatPouchGuideEditPcExtrasHtml()}<br/>${formatPouchGuideDeletePcExtrasHtml()}`;
}

/** PC: パウチホバー時の矢印（kbd、値行・数量行・丸め行をそれぞれ横並び。Long Press 行は含めない） */
export function formatPouchGuideArrowValueMultiplierPcHtml(): string {
  if (!isGuideTooltipActive()) return '';
  const tt = getTooltipCopy();
  const kd = (s: string) => `<kbd>${s}</kbd>`;
  const rowVal = `${kd('↑')} <span class="tt-desc">${escapeHtml(tt.valuePlus)}</span>\u3000${kd('↓')} <span class="tt-desc">${escapeHtml(tt.valueMinus)}</span>`;
  const rowMul = `${kd('←')} <span class="tt-desc">${escapeHtml(tt.multiMinus)}</span>\u3000${kd('→')} <span class="tt-desc">${escapeHtml(tt.multiPlus)}</span>`;
  const mod = getModifierKeys().altControl;
  const rowRound = `${kd(mod)}${kd('←')} <span class="tt-desc">${escapeHtml(tt.roundMinus)}</span>\u3000${kd(mod)}${kd('→')} <span class="tt-desc">${escapeHtml(tt.roundPlus)}</span>`;
  return `${rowVal}<br/>${rowMul}<br/>${rowRound}`;
}

/**
 * 数量≠×1 のとき: ×表示なら <kbd>*</kbd><kbd>*</kbd>、÷表示なら <kbd>/</kbd><kbd>/</kbd> で値へ反映。
 */
export function formatReflectQtyToValueFormulaGuideHtml(isDivisionDisplay: boolean): string {
  if (!isGuideTooltipActive()) return '';
  const tt = getTooltipCopy();
  const keys = isDivisionDisplay
    ? '<kbd>/</kbd><kbd>/</kbd>'
    : '<kbd>*</kbd><kbd>*</kbd>';
  return `${keys} <span class="tt-desc">${escapeHtml(tt.reflectQtyToValue)}</span>`;
}

/** Enter / 0~9 / - — Create Pouch（グループ・計算エリアのガイド共通。Edit 行と表記を揃える） */
export function formatEnterAndDigitsCreatePouchHtml(): string {
  if (!isGuideTooltipActive()) return '';
  const tt = getTooltipCopy();
  return `${formatTooltip('Enter')}/<kbd>0</kbd>~<kbd>9</kbd>/<kbd>-</kbd> <span class="tt-desc">${escapeHtml(tt.createPouch)}</span>`;
}

/** F2 / Space — Edit Sheet Title（計算エリアなど） */
export function formatSheetTitleGuideShortcutHtml(): string {
  if (!isGuideTooltipActive()) return '';
  const tt = getTooltipCopy();
  return `${formatTooltip('F2')} / ${formatTooltip('Space')} <span class="tt-desc">${escapeHtml(tt.editSheetTitle)}</span>`;
}

/** 計算エリア余白ガイド: レイヤー（Mac は ⌥ = Option / Win は Alt） */
export function formatLayerGuideShortcutsHtml(): string {
  if (!isGuideTooltipActive()) return '';
  const tt = getTooltipCopy();
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  const alt = isMac ? '⌥' : 'Alt';
  const kd = (s: string) => `<kbd>${s}</kbd>`;
  return [
    `${kd(alt)}+<kbd>1</kbd>~<kbd>9</kbd> <span class="tt-desc">${escapeHtml(tt.switchLayer)}</span>`,
    `${kd(alt)}+<kbd>←</kbd> / ${kd(alt)}+<kbd>→</kbd> <span class="tt-desc">${escapeHtml(tt.previousNextLayer)}</span>`,
    `${kd(alt)}+<kbd>+</kbd> <span class="tt-desc">${escapeHtml(tt.addLayerEditName)}</span>`,
    `${kd(alt)}+<kbd>−</kbd> <span class="tt-desc">${escapeHtml(tt.deleteLayerPressTwice)}</span>`,
  ].join('<br/>');
}

/** レイヤー一覧: 非アクティブ項目 */
export function formatLayerInactiveChipGuideHtml(): string {
  if (!isGuideTooltipActive()) return '';
  const tt = getTooltipCopy();
  return formatKeyDescLine('Click', escapeHtml(tt.selectLayer));
}

/** レイヤー一覧: アクティブ項目（名前編集） */
export function formatLayerActiveChipGuideHtml(): string {
  if (!isGuideTooltipActive()) return '';
  const tt = getTooltipCopy();
  return formatKeyDescLine('Click', escapeHtml(tt.editLayerName));
}

/** レイヤー一覧: 名前編集中 */
export function formatLayerNameEditingGuideHtml(includeTab: boolean): string {
  if (!isGuideTooltipActive()) return '';
  const tt = getTooltipCopy();
  const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const lines = [
    formatKeyDescLine('Enter', esc(tt.commitName)),
    formatKeyDescLine('Esc', esc(tt.discard)),
  ];
  if (includeTab) {
    lines.push(formatKeyDescLine('Tab', esc(tt.nextLayerEditName)));
    lines.push(
      `${formatTooltip('Shift')}${formatTooltip('Tab')} <span class="tt-desc">${esc(tt.previousLayerEditName)}</span>`
    );
  }
  return lines.join('<br/>');
}

/** レイヤー一覧: + ボタン */
export function formatLayerAddButtonGuideHtml(): string {
  if (!isGuideTooltipActive()) return '';
  const tt = getTooltipCopy();
  return formatKeyDescLine('Click', escapeHtml(tt.addLayer));
}

/** レイヤー一覧: − ボタン（二段階削除。シート削除と同表記） */
export function formatLayerRemoveButtonGuideHtml(): string {
  if (!isGuideTooltipActive()) return '';
  const tt = getTooltipCopy();
  return formatKeyDescLine('Double Click', escapeHtml(tt.deleteLayer));
}

/** テーブル見出し: レイヤー列 */
export function formatLayerTableHeaderCellGuideHtml(_isActive = false): string {
  if (!isGuideTooltipActive()) return '';
  const tt = getTooltipCopy();
  return [
    formatKeyDescLine('Click', escapeHtml(tt.selectLayer)),
    formatKeyDescLine('Double Click', escapeHtml(tt.editLayerName)),
  ].join('<br/>');
}

/** レイヤー一覧ホバー 2 秒後（キーボードショートカット追記） */
export function formatLayerListExtendedGuideHtml(includeTab: boolean): string {
  if (!isGuideTooltipActive()) return '';
  const shortcuts = formatLayerGuideShortcutsHtml();
  if (!includeTab) return shortcuts;
  const tabBlock = formatLayerNameEditingGuideHtml(true)
    .split('<br/>')
    .slice(2)
    .join('<br/>');
  return [shortcuts, tabBlock].filter(Boolean).join('<br/>');
}

/**
 * PC: テーブル選択中のパウチ色ガイド（2 秒ホバー追記）。
 * 計算エリアの PouchComponent.pouchGuideColorExtrasHtml と同一の表記・順序（C → Shift+C → 直接キー）。
 * @param currentPouchColorId 代表色（選択パウチ先頭の色など）。未使用時は POUCH_COLORS[0] 相当で解決。
 */
export function formatTableGuidePouchColorKeysPcHtml(shiftKeyDisplay: string, currentPouchColorId: string): string {
  if (!isGuideTooltipActive()) return '';
  const rawIndex = POUCH_COLORS.findIndex(c => c.id === currentPouchColorId);
  const currentIndex = rawIndex >= 0 ? rawIndex : 0;
  const nextIndex = (currentIndex + 1) % POUCH_COLORS.length;
  const prevIndex = (currentIndex - 1 + POUCH_COLORS.length) % POUCH_COLORS.length;
  const colorCircle = (idx: number) => {
    const color = POUCH_COLORS[idx];
    return `<span style="color:${color.value};font-size:1.3em;line-height:1;vertical-align:-0.05em">●</span>`;
  };
  const nextCircle = colorCircle(nextIndex);
  const prevCircle = colorCircle(prevIndex);
  const hotkeyRow = `<span class="tt-desc">${POUCH_COLOR_HOTKEY_DISPLAY_ORDER.map(({ letter, colorId }) => {
    const idx = POUCH_COLORS.findIndex(c => c.id === colorId);
    const circ = idx >= 0 ? colorCircle(idx) : '';
    return `<kbd>${letter}</kbd>${circ}`;
  }).join(' ')}</span>`;
  const locale = getActiveLocale();
  const rotateNext = `${formatTooltip('C')} <span class="tt-desc">${changePouchColor(nextCircle, locale)}</span>`;
  const rotatePrev = `${formatTooltip(shiftKeyDisplay)}${formatTooltip('C')} <span class="tt-desc">${changePouchColor(prevCircle, locale)}</span>`;
  return `${rotateNext}<br/>${rotatePrev}<br/>${hotkeyRow}`;
}

/**
 * PC: テーブル選択中のグループ色ガイド（2 秒ホバー追記）。
 * 計算エリアの GroupComponent.groupGuideColorExtrasHtml と同一の表記・順序（C → Shift+C → 直接キー）。
 * @param options.tableNoMute テーブル専用: M 省略・ローテは固体色のみ（テーブルは isMuteGroup でミュート行を出さない）
 */
export function formatTableGuideGroupColorKeysPcHtml(
  shiftKeyDisplay: string,
  groupColorId: string,
  options?: { tableNoMute?: boolean },
): string {
  if (!isGuideTooltipActive()) return '';
  const MUTE_COLOR = '#666666';
  const sym = (color: string, ch: string) =>
    `<span style="color:${color};font-size:1.3em;line-height:1;vertical-align:-0.05em">${ch}</span>`;
  const colorCircle = (idx: number) => {
    const c = GROUP_COLORS[idx];
    return `<span style="color:${c.value};font-size:1.3em;line-height:1;vertical-align:-0.05em">■</span>`;
  };

  const locale = getActiveLocale();
  if (options?.tableNoMute) {
    const n = GROUP_COLORS.length;
    const solidLabel = (gi: number) =>
      changeGroupColor(sym(GROUP_COLORS[gi].value, '■'), locale);
    const solidIndex = (() => {
      const i = GROUP_COLORS.findIndex(c => c.id === groupColorId);
      return i >= 0 ? i : 0;
    })();
    const nextIndex = (solidIndex + 1) % n;
    const prevIndex = (solidIndex - 1 + n) % n;
    const rotateNext = `${formatTooltip('C')} <span class="tt-desc">${solidLabel(nextIndex)}</span>`;
    const rotatePrev = `${formatTooltip(shiftKeyDisplay)}${formatTooltip('C')} <span class="tt-desc">${solidLabel(prevIndex)}</span>`;
    const directRow = `<span class="tt-desc">${GROUP_COLOR_HOTKEY_DISPLAY_ORDER.filter(({ colorId }) => colorId !== 'mute')
      .map(({ letter, colorId }) => {
        const idx = GROUP_COLORS.findIndex(c => c.id === colorId);
        const circ = idx >= 0 ? colorCircle(idx) : '';
        return `<kbd>${letter}</kbd>${circ}`;
      })
      .join(' ')}</span>`;
    return `${rotateNext}<br/>${rotatePrev}<br/>${directRow}`;
  }

  const allColors = [...GROUP_COLORS.map(c => ({ id: c.id, value: c.value })), { id: 'mute', value: MUTE_COLOR }];
  const currentColor = groupColorId || GROUP_COLORS[0].id;
  const currentIndex = allColors.findIndex(c => c.id === currentColor);
  const resolvedIndex = currentIndex >= 0 ? currentIndex : 0;
  const nextIndex = (resolvedIndex + 1) % allColors.length;
  const prevIndex = (resolvedIndex - 1 + allColors.length) % allColors.length;
  const colorLabel = (idx: number) =>
    allColors[idx].id === 'mute'
      ? enableMuteSymbol(sym(MUTE_COLOR, '▩'), locale)
      : changeGroupColor(sym(allColors[idx].value, '■'), locale);
  const rotateNext = `${formatTooltip('C')} <span class="tt-desc">${colorLabel(nextIndex)}</span>`;
  const rotatePrev = `${formatTooltip(shiftKeyDisplay)}${formatTooltip('C')} <span class="tt-desc">${colorLabel(prevIndex)}</span>`;
  const directRow = `<span class="tt-desc">${GROUP_COLOR_HOTKEY_DISPLAY_ORDER.map(({ letter, colorId }) => {
    if (colorId === 'mute') {
      return `<kbd>${letter}</kbd><span style="color:${MUTE_COLOR};font-size:1.3em;line-height:1;vertical-align:-0.05em">▩</span>`;
    }
    const idx = GROUP_COLORS.findIndex(c => c.id === colorId);
    const circ = idx >= 0 ? colorCircle(idx) : '';
    return `<kbd>${letter}</kbd>${circ}`;
  }).join(' ')}</span>`;
  return `${rotateNext}<br/>${rotatePrev}<br/>${directRow}`;
}

/** PC 操作ガイド: グループ — Edit 系 */
export function formatGroupGuideEditPcExtrasHtml(): string {
  if (!isGuideTooltipActive()) return '';
  const tt = getTooltipCopy();
  return [
    formatEnterAndDigitsCreatePouchHtml(),
    `${formatTooltip('F2')} / ${formatTooltip('Space')} <span class="tt-desc">${escapeHtml(tt.editGroupTitle)}</span>`,
  ].join('<br/>');
}

/** PC 操作ガイド: グループ — Delete 系 */
export function formatGroupGuideDeletePcExtrasHtml(): string {
  if (!isGuideTooltipActive()) return '';
  const tt = getTooltipCopy();
  return `${formatTooltip('Del')} / ${formatGuideBackspaceKeyHtml()} <span class="tt-desc">${escapeHtml(tt.deleteGroup)}</span>`;
}

/** PC 操作ガイド: グループ／リサイズ 2 秒後のキーボード行（Edit → Delete ※色は別ブロック） */
export function formatGroupGuidePcExtrasHtml(): string {
  if (!isGuideTooltipActive()) return '';
  return `${formatGroupGuideEditPcExtrasHtml()}<br/>${formatGroupGuideDeletePcExtrasHtml()}`;
}

export function formatTooltip(text: string): string {
  if (!text) return '';
  if (!isGuideTooltipActive()) return '';

  const lines = text.split('\n').map(line => {
    const colonIdx = line.indexOf(': ');
    if (colonIdx === -1) {
      return wrapKbd(escapeHtml(line));
    }

    const keyPart = line.slice(0, colonIdx);
    const descPart = line.slice(colonIdx + 2);

    return `${wrapKbd(escapeHtml(keyPart))} <span class="tt-desc">${escapeHtml(descPart)}</span>`;
  });

  return lines.join('<br/>');
}

/** 単一キー表記 + 説明（tt-desc）。`formatTooltip('X')` + tt-desc の合成漏れ防止用 */
export function formatKeyDescLine(key: string, descHtml: string): string {
  if (!isGuideTooltipActive()) return '';
  return `${formatTooltip(key)} <span class="tt-desc">${descHtml}</span>`;
}

/** 左側を formatTooltip で組んだ HTML + 説明 */
export function formatKeysLeftWithDesc(leftKeysHtml: string, descHtml: string): string {
  if (!isGuideTooltipActive()) return '';
  if (!leftKeysHtml) return '';
  return `${leftKeysHtml} <span class="tt-desc">${descHtml}</span>`;
}
